/** The compose LIBRARY: the module vocabulary and template catalog one game
 * is validated, compiled, proved and run against. The maintained library is
 * the repo's catalog (`registry.ts`, `templates/`); a game that carries
 * modules or templates from outside the repo (generated for it, or promoted
 * into the library store) runs against the maintained library extended with
 * exactly those entries. Every compose entry point is bound to one library
 * (`api.ts` `composeApi`), so no step of a build can silently resolve a
 * module from a different vocabulary than another step. */
import type { AnyComposeModule } from './module.js';
import { COMPOSE_MODULES } from './registry.js';
import { COMPOSE_TEMPLATES, type TemplateV1 } from './templates/index.js';
import { RETIRED_TEMPLATES } from './templates/retired.js';

export interface ComposeLibraryV1 {
  readonly modules: ReadonlyMap<string, AnyComposeModule>;
  readonly templates: readonly TemplateV1[];
}

/** The repo catalog: the current templates plus every retired revision (for
 * the games accepted on it; never listed for new Creates). */
export const MAINTAINED_LIBRARY: ComposeLibraryV1 = Object.freeze({ modules: COMPOSE_MODULES,
  templates: Object.freeze([...COMPOSE_TEMPLATES, ...RETIRED_TEMPLATES]) });

/** `base` plus `modules` and `templates`. An id already in the library is
 * rejected: an entry never shadows another, so a module id names one
 * implementation in every library that holds it. */
export function extendLibrary(base: ComposeLibraryV1, additions: { modules?: readonly AnyComposeModule[]; templates?: readonly TemplateV1[] }): ComposeLibraryV1 {
  const modules = new Map(base.modules);
  for (const module of additions.modules ?? []) {
    if (modules.has(module.id)) throw new RangeError(`library already holds module ${module.id}`);
    modules.set(module.id, module);
  }
  const templates = [...base.templates];
  for (const template of additions.templates ?? []) {
    if (templates.some(t => t.id === template.id)) throw new RangeError(`library already holds template ${template.id}`);
    templates.push(template);
  }
  return Object.freeze({ modules, templates: Object.freeze(templates) });
}
